import { Component, HostListener, Input, OnDestroy } from '@angular/core';

import * as feather from 'feather-icons';
import { Auth } from '@angular/fire/auth';
import { LoginLinkComponent } from '../login-link/login-link.component';
import { AppLanguageComponent } from '../app-language/app-language.component';
import { TranslateModule } from '@ngx-translate/core';
import { RouterModule } from '@angular/router';
import { ThemeService } from '../../services/theme.service';

/** Below Tailwind's `sm` the bar keeps the logo, the shops link and the menu button; language and theme move into the menu. */
const MOBILE_QUERY = '(max-width: 639.98px)';

@Component({
   selector: 'app-navbar',
   standalone: true,
   imports: [AppLanguageComponent, LoginLinkComponent, RouterModule, TranslateModule],
   templateUrl: './navbar.component.html',
   styleUrl: './navbar.component.scss',
})
export class NavbarComponent implements OnDestroy {
   @Input() currentSection?: string;
   userDisplayName: string = '';
   cookiesAccepted: boolean = false;
   menuOpen = false;
   // The language picker and theme toggle are rendered in one place only (bar or menu), so
   // #language-picker stays unique; that needs the breakpoint in code, not CSS.
   isMobile = false;
   private mobileQuery?: MediaQueryList;
   private onMobileChange = (e: MediaQueryListEvent) => {
      this.isMobile = e.matches;
      this.menuOpen = false;
   };

   constructor(private auth: Auth, public theme: ThemeService) {}

   ngOnInit() {
      this.auth.onAuthStateChanged((user) => {
         this.userDisplayName = user?.displayName || '';
         this.cookiesAccepted = !!localStorage.getItem('cookiesAccepted');
      });
      if (typeof window !== 'undefined' && window.matchMedia) {
         this.mobileQuery = window.matchMedia(MOBILE_QUERY);
         this.isMobile = this.mobileQuery.matches;
         this.mobileQuery.addEventListener('change', this.onMobileChange);
      }
   }

   ngOnDestroy(): void {
      this.mobileQuery?.removeEventListener('change', this.onMobileChange);
   }

   /** First letter of the name, shown in the avatar instead of an icon. One letter, as
    *  Google does: two identical ones ("Ivaylo Ivanov" → "II") read as a pause icon. */
   get initials(): string {
      return (Array.from(this.userDisplayName.trim())[0] ?? '').toUpperCase();
   }

   toggleMenu(event: Event): void {
      event.stopPropagation();
      this.menuOpen = !this.menuOpen;
   }

   @HostListener('document:click')
   closeMenu(): void {
      this.menuOpen = false;
   }

   @HostListener('document:keydown.escape')
   closeMenuOnEscape(): void {
      this.menuOpen = false;
   }

   logout(): void {
      this.menuOpen = false;
      this.auth.signOut();
   }

   acceptCookies(): void {
      // Save the user's consent in localStorage
      localStorage.setItem('cookiesAccepted', 'true');
      this.cookiesAccepted = true;
   }

   ngAfterViewInit(): void {
      feather.replace();
   }
}
